import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../ui/command';
import LoadingRing from '../ui-components/LoadingRing';
import { cn } from '../../lib/utils';
import { useToast } from '../../hooks/use-toast';
import {
    addScannersToFolder,
    removeScannersFromFolder,
    ScannerMember,
} from '../../services/api/folderService';
import { getAllMRIMachinesByDepartment } from '../../services/apiService';
import { buildScannerSecondaryLine } from './ScannerMemberItem';

interface LinkedScannersDialogProps {
    folderId: string;
    existingScanners: ScannerMember[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onChanged: () => void;
}

interface ScannerRowProps {
    scanner: ScannerMember;
    variant: 'existing' | 'pending';
    actionLabel: string;
    onAction: (id: string) => void;
    loading?: boolean;
}

const ScannerRow = ({
    scanner,
    variant,
    actionLabel,
    onAction,
    loading,
}: ScannerRowProps) => {
    const { t } = useTranslation();
    const name = scanner.Name ?? t('folders.linkedScanners.unnamed');
    return (
        <li
            className={cn(
                'flex items-center justify-between gap-3 rounded-lg border border-subtle bg-card px-4 py-3',
                variant === 'pending' && 'border-dashed'
            )}
        >
            <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">
                    {name}
                </div>
                <div className="truncate text-xs text-subtle-foreground">
                    {buildScannerSecondaryLine(scanner)}
                </div>
            </div>
            <Button
                variant="outline"
                size="sm"
                className={
                    variant === 'existing' ? 'hover:text-destructive' : undefined
                }
                disabled={loading}
                onClick={() => onAction(scanner._id)}
            >
                {loading ? (
                    <LoadingRing size="small" />
                ) : (
                    <>
                        <X
                            className="mr-1.5 h-3.5 w-3.5"
                            aria-hidden="true"
                        />
                        {actionLabel}
                    </>
                )}
            </Button>
        </li>
    );
};

export const LinkedScannersDialog = ({
    folderId,
    existingScanners,
    open,
    onOpenChange,
    onChanged,
}: LinkedScannersDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [allScanners, setAllScanners] = useState<ScannerMember[]>([]);
    const [pendingScanners, setPendingScanners] = useState<
        Map<string, ScannerMember>
    >(new Map());
    const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [addPopoverOpen, setAddPopoverOpen] = useState(false);

    useEffect(() => {
        if (!open) {
            setPendingScanners(new Map());
            setPendingRemoveId(null);
            setAddPopoverOpen(false);
            return;
        }
        const load = async () => {
            setLoading(true);
            try {
                const data = await getAllMRIMachinesByDepartment();
                setAllScanners(Array.isArray(data) ? data : []);
            } catch {
                toast({
                    title: t('folders.common.error'),
                    description: t('folders.linkedScanners.toasts.loadFailed'),
                    variant: 'destructive',
                });
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [open]);

    const linkedIds = useMemo(
        () => new Set(existingScanners.map((s) => s._id)),
        [existingScanners]
    );

    const availableScanners = allScanners.filter(
        (s) => !linkedIds.has(s._id) && !pendingScanners.has(s._id)
    );

    const togglePending = (scanner: ScannerMember) => {
        setPendingScanners((prev) => {
            const next = new Map(prev);
            if (next.has(scanner._id)) next.delete(scanner._id);
            else next.set(scanner._id, scanner);
            return next;
        });
    };

    const undoPending = (scannerId: string) => {
        setPendingScanners((prev) => {
            const next = new Map(prev);
            next.delete(scannerId);
            return next;
        });
    };

    const handleRemove = async (scannerId: string) => {
        setPendingRemoveId(scannerId);
        try {
            await removeScannersFromFolder(folderId, [scannerId]);
            onChanged();
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.linkedScanners.toasts.removeFailed'),
                variant: 'destructive',
            });
        } finally {
            setPendingRemoveId(null);
        }
    };

    const handleSubmit = async () => {
        if (pendingScanners.size === 0) return;
        setSubmitting(true);
        try {
            await addScannersToFolder(
                folderId,
                Array.from(pendingScanners.keys())
            );
            const count = pendingScanners.size;
            toast({
                title: t('folders.linkedScanners.addedToast', { count }),
            });
            onChanged();
            onOpenChange(false);
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.linkedScanners.toasts.addFailed'),
                variant: 'destructive',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const selectedCount = pendingScanners.size;
    const pendingList = Array.from(pendingScanners.values());
    const isEmpty = existingScanners.length === 0 && pendingList.length === 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>
                        {t('folders.linkedScanners.title')}
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <LoadingRing size="small" />
                    </div>
                ) : (
                    <div className="space-y-3">
                        {isEmpty ? (
                            <p className="py-4 text-sm text-subtle-foreground">
                                {t('folders.linkedScanners.emptyLinked')}
                            </p>
                        ) : (
                            <ul className="max-h-72 space-y-2 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                                {existingScanners.map((scanner) => {
                                    const isRemoving =
                                        pendingRemoveId === scanner._id;
                                    return (
                                        <ScannerRow
                                            key={scanner._id}
                                            scanner={scanner}
                                            variant="existing"
                                            actionLabel={t(
                                                'folders.linkedScanners.remove'
                                            )}
                                            onAction={handleRemove}
                                            loading={isRemoving}
                                        />
                                    );
                                })}
                                {pendingList.map((scanner) => (
                                    <ScannerRow
                                        key={scanner._id}
                                        scanner={scanner}
                                        variant="pending"
                                        actionLabel={t(
                                            'folders.linkedScanners.undo'
                                        )}
                                        onAction={undoPending}
                                    />
                                ))}
                            </ul>
                        )}

                        {availableScanners.length > 0 && (
                            <Popover
                                open={addPopoverOpen}
                                onOpenChange={setAddPopoverOpen}
                            >
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="mt-2 w-full"
                                    >
                                        <Plus
                                            className="mr-2 h-3.5 w-3.5"
                                            aria-hidden="true"
                                        />
                                        {t('folders.linkedScanners.addScanner')}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                    align="start"
                                    className="w-[var(--radix-popover-trigger-width)] p-0"
                                >
                                    <Command>
                                        <CommandInput
                                            placeholder={t(
                                                'folders.linkedScanners.search'
                                            )}
                                        />
                                        <CommandList
                                            onWheel={(e) =>
                                                e.stopPropagation()
                                            }
                                        >
                                            <CommandEmpty>
                                                {t(
                                                    'folders.linkedScanners.noMatching'
                                                )}
                                            </CommandEmpty>
                                            <CommandGroup>
                                                {availableScanners.map(
                                                    (scanner) => (
                                                        <CommandItem
                                                            key={scanner._id}
                                                            value={`${scanner.Name ?? ''} ${scanner.Model ?? ''} ${scanner.Manufacturer ?? ''} ${scanner._id}`}
                                                            onSelect={() => {
                                                                togglePending(
                                                                    scanner
                                                                );
                                                                setAddPopoverOpen(
                                                                    false
                                                                );
                                                            }}
                                                        >
                                                            <div className="flex w-full flex-col items-start gap-0.5">
                                                                <span className="truncate text-sm font-medium">
                                                                    {scanner.Name ??
                                                                        t(
                                                                            'folders.linkedScanners.unnamed'
                                                                        )}
                                                                </span>
                                                                <span className="truncate text-xs text-subtle-foreground">
                                                                    {buildScannerSecondaryLine(
                                                                        scanner
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </CommandItem>
                                                    )
                                                )}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        )}
                    </div>
                )}

                <DialogFooter className="items-center justify-between sm:justify-between">
                    <span className="text-xs text-subtle-foreground">
                        {selectedCount > 0 &&
                            t('folders.linkedScanners.selected', {
                                count: selectedCount,
                            })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            variant={selectedCount > 0 ? 'outline' : 'default'}
                            onClick={() => onOpenChange(false)}
                            disabled={submitting}
                        >
                            {selectedCount > 0
                                ? t('folders.linkedScanners.cancel')
                                : t('folders.linkedScanners.done')}
                        </Button>
                        {selectedCount > 0 && (
                            <Button onClick={handleSubmit} disabled={submitting}>
                                {submitting ? (
                                    <LoadingRing size="small" />
                                ) : (
                                    t('folders.linkedScanners.add', {
                                        count: selectedCount,
                                    })
                                )}
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
